import {
  DebtRepaymentPlan,
  Disclaimer,
  Div,
  P,
  Pill,
  ReceivablesLocation,
  TooltipWrapper,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { formatDayTime } from '../helpers/receivablesLabels';
import { planMethodsLine } from '../helpers/repaymentPlanLabels';
import { StatusPill } from './ReceivablesCells';

/*
 * The values of one receivables location, shared by its desktop row and its
 * phone card.
 */

/**
 * The old vDrive debt (the items a repayment plan pays back), and the plan's
 * tag when one is running. The methods are in the tag's tooltip.
 */
export const OldVDriveDebt = ({ row }: { row: ReceivablesLocation }) => (
  // Desktop: the tag under the amount, always - beside it, it fit on wide
  // screens and wrapped on laptops, so rows looked different from width to
  // width. Phone card: beside the amount when it fits, under it when not;
  // a card stands alone, there is no row next to it to line up with.
  <Div
    $flex
    $column
    $alignStart
    $gap="4px"
    $rowSM
    $alignCenterSM
    $wrapSM
    $gapSM="4px 8px"
  >
    {row.vDriveItems > 0 ? (
      <P $m="0" $bold>
        {displayCentsInDollars(row.vDriveItems)}
      </P>
    ) : (
      <Disclaimer $m="0" $color={lightTheme.lightGray}>
        -
      </Disclaimer>
    )}
    {row.debtRepaymentPlan && <PlanTag plan={row.debtRepaymentPlan} />}
  </Div>
);

/**
 * A running repayment plan, as a tinted tag like the provider tags on the
 * Finances tab (FinanceTabbedCard); the methods are in its tooltip.
 */
const PlanTag = ({ plan }: { plan: DebtRepaymentPlan }) => (
  <TooltipWrapper tooltip={`Repayment plan: ${planMethodsLine(plan)}`}>
    <Pill
      $background={lightTheme.transparentGreen}
      $minWidth="0"
      $minWidthSM="0"
      $height="20px"
      $padding="0 8px"
      // Block-level flex, not Pill's inline-flex: inside the tooltip's
      // link an inline pill sat on a text baseline and ended up 1px
      // below the amount it belongs to.
      style={{ flex: 'none', display: 'flex' }}
    >
      <Disclaimer $m="0" $bold $noWrap $color={lightTheme.green}>
        Repayment plan
      </Disclaimer>
    </Pill>
  </TooltipWrapper>
);

/**
 * What the location owes besides the old vDrive debt (that has its own
 * column), one line per kind, then where its vDrive reserve
 * stands. The reserve line carries the limit and what is left: "vDrive
 * reserve used: $120.00 of $300.00 ($180.00 left)" while it owes the reserve,
 * "Reserve left $300.00 of $300.00" on any other vDrive location. A balance
 * shared by several locations counts what is used on one of them only (so
 * the lines add up to the cards); every one of them says who it shares with.
 */
export const OwedBreakdown = ({ row }: { row: ReceivablesLocation }) => {
  const hasReserve =
    row.vDriveReserveLimit != null && row.vDriveReserveLeft != null;
  const reserve = hasReserve
    ? `${displayCentsInDollars(row.vDriveOverdraft)} of ${displayCentsInDollars(
        row.vDriveReserveLimit as number
      )} (${displayCentsInDollars(row.vDriveReserveLeft as number)} left)`
    : displayCentsInDollars(row.vDriveOverdraft);
  const sharedWith = row.vDriveSharedWith ?? [];

  const parts = [
    {
      label: 'vDrive reserve used',
      amount: row.vDriveOverdraft,
      text: reserve,
    },
    {
      label: 'SaaS',
      amount: row.saasItems,
      text: displayCentsInDollars(row.saasItems),
      plan: row.saasDebtRepaymentPlan,
    },
  ].filter((part) => part.amount > 0);

  // The reserve without a debt line of its own: a location owing nothing, or
  // one whose shared balance is counted on another location.
  const reserveLeft =
    hasReserve && row.vDriveOverdraft <= 0
      ? `Reserve left ${displayCentsInDollars(
          row.vDriveReserveLeft as number
        )} of ${displayCentsInDollars(row.vDriveReserveLimit as number)}`
      : null;

  return (
    <>
      {/* Old vDrive debt alone is not "nothing": it has its own column.
          Then this one shows only the reserve, or a dash if not even that. */}
      {parts.length === 0 &&
        (row.vDriveItems <= 0 ? (
          <Disclaimer $m="0">Nothing owed</Disclaimer>
        ) : (
          !reserveLeft &&
          sharedWith.length === 0 && (
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              -
            </Disclaimer>
          )
        ))}
      {parts.map((part) =>
        // The SaaS plan's tag beside its line, wrapping under it when narrow.
        'plan' in part && part.plan ? (
          <Div key={part.label} $flex $alignCenter $wrap $gap="4px 8px">
            <Disclaimer $m="0">
              {part.label}: {part.text}
            </Disclaimer>
            <PlanTag plan={part.plan} />
          </Div>
        ) : (
          <Disclaimer key={part.label} $m="0">
            {part.label}: {part.text}
          </Disclaimer>
        )
      )}
      {reserveLeft && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          {reserveLeft}
        </Disclaimer>
      )}
      {sharedWith.length > 0 && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          Shared with {sharedWith.map((location) => location.name).join(', ')}
        </Disclaimer>
      )}
    </>
  );
};

/**
 * GoCardless's "expired" or "pending_submission" as "Expired", "Pending
 * submission"; "none" is a bank account with no mandate at all.
 */
const mandateStatusLabel = (status: string) => {
  if (status === 'none') return 'No mandate';
  const words = status.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const BankStatus = ({ row }: { row: ReceivablesLocation }) => {
  const status = !row.hasAch
    ? { text: 'No bank', color: lightTheme.lightGray }
    : !row.achMandateValid
    ? { text: 'Bank not active', color: lightTheme.primary }
    : { text: 'Bank active', color: lightTheme.green };

  // Why the bank stopped working, straight from GoCardless, so finance does
  // not have to go and look: the mandate's status under the pill ("Expired",
  // "Cancelled"), the reason and when it was read in the tooltip. A null
  // status means the backend has not checked this mandate yet.
  const inactive = row.hasAch && !row.achMandateValid;
  const mandateStatus = row.achMandateStatus
    ? mandateStatusLabel(row.achMandateStatus)
    : 'Status not checked yet';
  const mandateDetails = inactive
    ? [
        row.achMandateReason,
        row.achMandateCheckedAt &&
          `Checked ${formatDayTime(row.achMandateCheckedAt)}`,
      ]
        .filter(Boolean)
        .join('. ')
    : '';

  return (
    <Div $flex $column $alignStart $gap="4px" $rowSM $alignCenterSM $wrapSM>
      <TooltipWrapper tooltip={mandateDetails || undefined}>
        <StatusPill color={status.color}>{status.text}</StatusPill>
      </TooltipWrapper>
      {inactive && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          {mandateStatus}
        </Disclaimer>
      )}
      {row.pendingBankTotal > 0 && (
        <Disclaimer $m="0" $color={lightTheme.darkYellow}>
          {displayCentsInDollars(row.pendingBalanceCredit)} on its way (
          {displayCentsInDollars(row.pendingBankTotal)} with fees)
        </Disclaimer>
      )}
    </Div>
  );
};

/**
 * Whether the owner is told about the SaaS debt. The backend only shows it
 * to a location vGrubs is profitable for; when it is hidden, the owner has
 * no way to know, so support has to be the one to bring it up.
 */
export const SaasVisibility = ({ row }: { row: ReceivablesLocation }) => {
  if (row.saasProfitable === null) {
    return <StatusPill color={lightTheme.lightGray}>No SaaS debt</StatusPill>;
  }
  return row.saasProfitable ? (
    <StatusPill color={lightTheme.green}>Shown to owner</StatusPill>
  ) : (
    <StatusPill color={lightTheme.orange}>Hidden from owner</StatusPill>
  );
};
